import { Component, Input, Output, EventEmitter, AfterContentInit, OnChanges, SimpleChanges } from '@angular/core';
import { PlycaDataService } from '../../../services/plyca.service';
import {
  TenderingPhaseDTO,
  TenderingPhaseTypeDTO,
  TenderEvaluationCommitteeDTO,
  TenderingPhaseState,
  ProcurementCommitteeDTO
} from '../../api/plyca-dtos';
import { SelectItem, SortMeta } from 'primeng-lts/api';
import { Configuration } from '../../conf/app.constants';
import { TenderingBaseComponent } from './tenderingBase.component';
import { EnvelopeDTO, ValidationResultDTO, ResultTypeDTO, ValidationExecutionDTO } from '../../api/plyca-dtos-v2';
import { PaginationDTO } from '../../api/PaginationModel';
import { finalize, catchError, map, take } from 'rxjs/operators';
import { throwError, Observable } from 'rxjs';
import { HelpType } from '../../components/common/help/help.service';

const DATE_SORT_FIELD = 'initDateNullsLast';

@Component({
  selector: 'tenderingPhases',
  templateUrl: 'tenderingPhases.template.html'
})
export class TenderingPhasesComponent
  extends TenderingBaseComponent<TenderingPhaseDTO>
  implements AfterContentInit, OnChanges {
  @Input()
  public tenderEvaluationCommittees: Array<TenderEvaluationCommitteeDTO>;
  @Input()
  public tenderingEnvelopes: Array<EnvelopeDTO>;
  @Output()
  deletedCommitee = new EventEmitter<string>();
  @Output()
  dispatch = new EventEmitter<boolean>();

  private tenderingPhaseTypes: Array<SelectItem>;

  public expandedRows: any;
  public multiSortMeta: SortMeta[];

  constructor(
    private _plycaService: PlycaDataService,
    private configuration: Configuration
  ) {
    super();
    this.colHeaders = [
      'TenderingPhase.edit.type',
      'TenderingPhase.envelopes',
      'TenderingPhase.period',
      'General.state',
      'General.actions'
    ];
    this.multiSortMeta = [];
    // https://www.primefaces.org/primeng-v12-lts/#/table
    // Sort order as number, 1 for asc and -1 for dec in single sort mode
    this.multiSortMeta.push({ field: 'type.identifier', order: -1 });
    this.multiSortMeta.push({ field: DATE_SORT_FIELD, order: 1 });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['data']) {
      // actuar ante cambios en la variable de entrada data que contiene datos
      // de las fases
      if (changes['data'].currentValue) {
        // anyadir a cada objeto propiedad ordenacion asociada a una funcion
        changes['data'].currentValue.forEach((phase: TenderingPhaseDTO) => this.patchTenderingPhaseToOrder(phase));
        this.expandedRows = this.getExpandedRows();
      }
    }
  }


  private patchTenderingPhaseToOrder(phase: TenderingPhaseDTO): void {
    Object.defineProperty(phase, DATE_SORT_FIELD, {
      get: function () { return (this.initDate) ? this.initDate.getTime() : Number.MAX_VALUE; }
    });
  }

  ngAfterContentInit(): void {
    super.ngAfterContentInit();
    this.tenderingPhaseTypes = new Array<SelectItem>();
    this.subscribe(this._plycaService
      .query<TenderingPhaseTypeDTO>(
        this.configuration.TENDERPHASETYPE_SERVICE_URL
      ),
      (result: PaginationDTO<TenderingPhaseTypeDTO>) => {
        if (result) {
          result.content
            .map((phase: TenderingPhaseTypeDTO) => {
              return {
                label: phase.description,
                value: phase
              } as SelectItem;
            })
            .forEach((item: SelectItem) =>
              this.tenderingPhaseTypes.push(item)
            );
        }
      },
      (error: any) => this.showServerError(error),
      () => this.setLoaded(true)
    );
  }

  /**
   * Devuelve identificadores de tabla para expandir
   */
  private getExpandedRows() {
    let expandedRow: any;
    expandedRow = {};
    if (this.data) {
      this.data
        .forEach(
          (phase: TenderingPhaseDTO) => expandedRow[phase.identifier] = true
        );
    }
    return expandedRow;
  }

  getTagsIdentifier(): string[] {
    return [HelpType.EDIT_FASE_LIC];
  }

  getServiceURL(): string {
    return this.configuration.TENDERPHASE_SERVICE_URL;
  }

  getFormSettingURL(): string {
    return 'tenderingPhasesSettings.json';
  }

  getDialogTitle(): string {
    return 'TenderingPhase.edit.title';
  }

  public getEnvelopes(phase: TenderingPhaseDTO): string {
    return TenderingPhaseDTO.getEnvelopesDescription(phase);
  }

  public containEnvelopes(phase: TenderingPhaseDTO): boolean {
    // tslint:disable-next-line:triple-equals
    return phase.envelopes != undefined;
  }

  dtoToBean(dto: TenderingPhaseDTO) {
    let bean: any;
    bean = dto;
    let avaliableEnvelopes = this.tenderingEnvelopes
      .filter(
        (projectEnvelope: EnvelopeDTO) =>
          this.data.length === 0 ||
          !this.data.find(
            (phase: TenderingPhaseDTO) =>
              phase.identifier !== dto.identifier &&
              (phase.envelopes &&
                phase.envelopes.find(
                  (phaseEnvelope: EnvelopeDTO) =>
                    projectEnvelope.identifier === phaseEnvelope.identifier
                  // tslint:disable-next-line:triple-equals
                ) != undefined)
          )
      )
      .sort((a: EnvelopeDTO, b: EnvelopeDTO) => a.order - b.order)
      .map((envelope: EnvelopeDTO) => {
        return {
          label: envelope.description,
          value: envelope
        } as SelectItem;
      });
    bean.envelopeItems = avaliableEnvelopes;
    bean.tenderingPhaseTypeItems = this.tenderingPhaseTypes;
    return bean;
  }

  beanToDTO(bean: any): TenderingPhaseDTO {
    let dto = bean;
    // eliminar propiedades presentacion
    delete dto.envelopeItems;
    delete dto.tenderingPhaseTypeItems;
    if (dto.publications) {
      dto.publications.forEach((publication) => {
        delete publication.document;
      });
    }
    delete dto[DATE_SORT_FIELD];
    return bean as TenderingPhaseDTO;
  }

  public isPhaseClosed(phase: TenderingPhaseDTO): boolean {
    return phase.state === TenderingPhaseState.CLOSED;
  }

  public isPhasePublished(phase: TenderingPhaseDTO): boolean {
    return phase.state === TenderingPhaseState.PUBLISHED;
  }

  public isPhasePreviouslyPublished(phase: TenderingPhaseDTO): boolean {
    return phase.publications && phase.publications.length > 0;
  }

  public isEditingPhase(phase: TenderingPhaseDTO): boolean {
    return phase.state === TenderingPhaseState.CREATED;
  }

  public publishPhase(phase: TenderingPhaseDTO): void {
    this.waitService.startWaiting();
    this.subscribe(this._plycaService
      .execAction(
        this.configuration.TENDERPHASE_SERVICE_URL,
        phase.identifier,
        'TENDERINGPHASE_PUBLICATION'
      )
      .pipe(
        finalize(() => this.waitService.cancelWaiting())
      ),
      (result: any) => {
        if (result) {
          if ('OK' === result.identifier) {
            phase.publications = new Array();
            const ref = this.uiService.showMessage(
              this.translationService.instant('TenderingPhase.actions.published'),
              this.translationService.instant('General.info')
            );
            ref.onClose.pipe(take(1)).subscribe(() => this.dispatch.next(true));
          } else {
            this.uiService.showMessage(
              (result.description) ? result.description : this.translationService.instant('TenderingPhase.actions.error'),
              this.translationService.instant('General.error')
            );
            // lanzar la actualizacion y validacion de esta fase
            this.emitUpdate(phase.identifier);
          }
        }
      });
  }

  private unPublishPhase(phase: TenderingPhaseDTO): Observable<TenderingPhaseDTO> {
    return this.setTenderingPhaseState(phase.identifier, TenderingPhaseState.CREATED)
      .pipe(
        map(() => {
          phase.state = TenderingPhaseState.CREATED;
          return phase;
        })
      );
  }

  private setTenderingPhaseState(identifier: string, state: TenderingPhaseState): Observable<any> {
    let patchedPhase = new TenderingPhaseDTO();
    patchedPhase.identifier = identifier;
    patchedPhase.state = state;
    return this._plycaService.patch<TenderingPhaseDTO>(
      this.getServiceURL(),
      patchedPhase
    ).pipe(
      finalize(() => this.updated.emit(identifier)),
      catchError((error: any) => {
        this.showServerError(error);
        return throwError(error);
      })
    );
  }

  public getStateDescription(phase: TenderingPhaseDTO): string {
    return this.translationService.instant(
      'TenderingPhase.state.' + phase.state
    );
  }

  public emitDeletedCommitee(identifier: string): void {
    this.deletedCommitee.next(identifier);
  }

  public editData(phase?: TenderingPhaseDTO): void {
    if (phase && !this.isEditingPhase(phase)) {
      let msg = '<span class ="text-center">'
        + this.translationService.instant('TenderingPhase.warn.publishedEdition', { state: this.getStateDescription(phase) })
        + '</span>';
      this.uiService.showConfirmMessage({
        message: msg,
        header: this.translationService.instant('General.warn'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.waitService.startWaiting();
          phase.state = TenderingPhaseState.CREATED;
          this.subscribe(this.unPublishPhase(phase)
            .pipe(catchError((error: any) => {
              this.waitService.cancelWaiting();
              return throwError(error);
            })),
            (patchedPhase: TenderingPhaseDTO) => super.editData(patchedPhase));
        },
        reject: () => {

        }
      });
    } else {
      super.editData(phase);
    }
  }

  public showNotContainsEnvelope(): void {
    let validation = new ValidationResultDTO();
    validation.result = ResultTypeDTO.WARNING;
    validation.errors = [];
    let error = new ValidationExecutionDTO();
    error.message = this.translationService.instant('TenderingPhase.warn.envelopeAssociation');
    validation.errors.push(error);
    this.showValidationResult(validation);
  }

  public getOrderedCommittees(phase: TenderingPhaseDTO): Array<ProcurementCommitteeDTO> {
    return (phase.openingCommittees) ?
      phase.openingCommittees.sort((a: ProcurementCommitteeDTO, b: ProcurementCommitteeDTO) => {
        // ordenar segun orden de los sobres
        let order = this.getEnvelopeOrder(a) - this.getEnvelopeOrder(b);
        if (order === 0) {
          order = this.dateToMilis(a.date) - this.dateToMilis(b.date);
        }
        return order;
      })
      : [];
  }

  private dateToMilis(date1: Date): number {
    return (date1) ? date1.getTime() : Number.MAX_VALUE;
  }

  private getEnvelopeOrder(commitee: ProcurementCommitteeDTO): number {
    return (commitee && commitee.envelopes && commitee.envelopes.length > 0) ? commitee.envelopes[0].order : Number.MAX_VALUE;
  }

}
